import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  Box, Container, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, TextField,
  InputAdornment, Chip, CircularProgress, Alert, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
  Grid, Stack, FormControl, InputLabel, Select, MenuItem, Divider,
} from '@mui/material';
import {
  Search, Clear, Add, DeleteOutline, ShoppingCartOutlined, Refresh,
  TrendingUp, AttachMoney, CheckCircle, Event, WarningAmber,
  ReceiptLong, LockOutlined, Close, Inventory2,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import {
  consumablesUrl,
  consumablePurchasesUrl,
  processConsumablePurchaseUrl,
  deleteConsumablePurchaseUrl,
} from '../BaseURL';

// ── Theme (matches Procurement/PurchaseOrders.js) ──────────────────────────
const PRIMARY      = '#41a386';
const PRIMARY_DARK = '#2f8068';
const BORDER       = '#e5e9ef';
const MUTED        = '#64748b';
const STRONG       = '#0f172a';
const LABEL_SX = {
  fontSize: '0.66rem', fontWeight: 700, color: '#94a3b8',
  textTransform: 'uppercase', letterSpacing: '0.6px',
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount || 0);

const isExpiringSoon = (dateStr) => {
  if (!dateStr) return false;
  const diff = (new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24);
  return diff >= 0 && diff <= 90;
};

const formatStockLine = (packs, loose, pack_label, base_unit) => {
  const p = parseInt(packs, 10) || 0;
  const l = parseInt(loose, 10) || 0;
  const parts = [];
  if (p > 0) parts.push(`${p} ${pack_label ? pack_label.toLowerCase() : 'pack'}${p === 1 ? '' : 's'}`);
  if (l > 0) parts.push(`${l} ${base_unit}${l === 1 ? '' : 's'}`);
  if (parts.length === 0) parts.push(`0 ${base_unit}`);
  return parts.join(' + ');
};

const ConsumablePurchaseOrders = () => {
  // ── List state ────────────────────────────────────────────────────────────
  const [batches, setBatches]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [page, setPage]           = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate]   = useState('');
  const [endDate, setEndDate]       = useState('');
  const [statusFilter, setStatusFilter] = useState('active');

  // ── Purchase dialog ──────────────────────────────────────────────────────
  const [openPurchaseDialog, setOpenPurchaseDialog] = useState(false);
  const [supplierName, setSupplierName]   = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [purchaseDate, setPurchaseDate]   = useState(() => new Date().toISOString().slice(0, 10));
  const [savingPurchase, setSavingPurchase] = useState(false);

  // Catalogue (left pane in dialog)
  const [consumables, setConsumables]           = useState([]);
  const [loadingConsumables, setLoadingConsumables] = useState(false);
  const [productSearchTerm, setProductSearchTerm]   = useState('');
  const [categoryFilter, setCategoryFilter]         = useState('all');

  // Active line entry
  const [selectedItem, setSelectedItem] = useState(null);
  const [linePacks, setLinePacks]       = useState('');
  const [lineLoose, setLineLoose]       = useState('');
  const [linePackCost, setLinePackCost] = useState('');
  const [lineUnitCost, setLineUnitCost] = useState('');
  const [lineExpiry, setLineExpiry]     = useState('');

  // Lines accumulated for this invoice
  const [purchaseItems, setPurchaseItems] = useState([]);

  // ── Current user (for received_by) ───────────────────────────────────────
  const currentUser = useMemo(() => {
    try {
      const raw = localStorage.getItem('userInfo');
      if (raw) {
        const u = JSON.parse(raw);
        return u?.username || u?.name || '';
      }
    } catch (_) {}
    return '';
  }, []);

  // ── Loaders ──────────────────────────────────────────────────────────────
  const loadBatches = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(consumablePurchasesUrl, {
        params: {
          search: searchTerm || undefined,
          from:   startDate || undefined,
          to:     endDate   || undefined,
          status: statusFilter,
        },
        timeout: 15000,
      });
      if (data?.success) {
        setBatches(data.results || []);
      } else {
        setError(data?.message || 'Failed to load batches');
        setBatches([]);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
      setBatches([]);
    } finally {
      setLoading(false);
    }
  };

  const loadConsumables = async () => {
    setLoadingConsumables(true);
    try {
      const { data } = await axios.get(consumablesUrl, { params: { status: 'active' } });
      if (data?.success) {
        setConsumables(data.results || []);
      } else {
        setConsumables([]);
      }
    } catch (err) {
      console.error('[consumable PO] catalogue load failed:', err.message);
      setConsumables([]);
    } finally {
      setLoadingConsumables(false);
    }
  };

  useEffect(() => { loadBatches(); /* eslint-disable-next-line */ }, [statusFilter]);
  // Debounce free-text + date changes
  useEffect(() => {
    const t = setTimeout(() => loadBatches(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [searchTerm, startDate, endDate]);

  useEffect(() => {
    if (openPurchaseDialog) loadConsumables();
  }, [openPurchaseDialog]);

  // ── Dashboard tiles ──────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const active = batches.filter(b => b.status === 'active');
    const totalQty   = active.reduce((s, b) => s + (Number(b.batch_qty)  || 0), 0);
    const totalValue = active.reduce((s, b) => s + (Number(b.total_cost) || 0), 0);
    const expiring   = active.filter(b => isExpiringSoon(b.expiry_date)).length;
    return {
      records: batches.length,
      totalQty,
      totalValue,
      expiring,
    };
  }, [batches]);

  // Filtered catalogue
  const filteredCatalogue = useMemo(() => {
    return consumables.filter(c => {
      const matchSearch = !productSearchTerm ||
        c.name?.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
        c.category?.toLowerCase().includes(productSearchTerm.toLowerCase());
      const matchCat = categoryFilter === 'all' || c.category === categoryFilter;
      return matchSearch && matchCat;
    });
  }, [consumables, productSearchTerm, categoryFilter]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    consumables.forEach(c => { if (c.category) set.add(c.category); });
    return Array.from(set).sort();
  }, [consumables]);

  // ── Dialog helpers ───────────────────────────────────────────────────────
  const resetLineForm = () => {
    setSelectedItem(null);
    setLinePacks('');
    setLineLoose('');
    setLinePackCost('');
    setLineUnitCost('');
    setLineExpiry('');
  };

  const closePurchaseDialog = () => {
    if (purchaseItems.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Discard this invoice?',
        text: `You have ${purchaseItems.length} line item${purchaseItems.length === 1 ? '' : 's'} pending. Closing will lose them.`,
        showCancelButton: true,
        confirmButtonText: 'Discard',
        confirmButtonColor: '#b91c1c',
      }).then((r) => {
        if (r.isConfirmed) {
          setPurchaseItems([]);
          resetLineForm();
          setSupplierName('');
          setInvoiceNumber('');
          setOpenPurchaseDialog(false);
        }
      });
    } else {
      resetLineForm();
      setSupplierName('');
      setInvoiceNumber('');
      setOpenPurchaseDialog(false);
    }
  };

  const handleAddLine = () => {
    if (!selectedItem) return;
    const packs = parseInt(linePacks, 10) || 0;
    const loose = parseInt(lineLoose, 10) || 0;
    if (packs === 0 && loose === 0) {
      Swal.fire({ icon: 'warning', title: 'Quantity required', text: 'Enter at least 1 pack or 1 loose piece.' });
      return;
    }
    const packCost = parseFloat(linePackCost) || 0;
    let unitCost = parseFloat(lineUnitCost) || 0;
    if (!unitCost && packCost && selectedItem.pack_size > 1) {
      unitCost = packCost / selectedItem.pack_size;
    }
    if (!unitCost && !packCost) {
      Swal.fire({ icon: 'warning', title: 'Cost required', text: 'Enter a cost per pack or per unit.' });
      return;
    }
    const batchQty = packs * (selectedItem.pack_size || 1) + loose;
    const totalCost = batchQty * unitCost;
    setPurchaseItems((prev) => [...prev, {
      consumableId: selectedItem.id,
      name:         selectedItem.name,
      base_unit:    selectedItem.base_unit,
      pack_label:   selectedItem.pack_label,
      pack_size:    selectedItem.pack_size,
      packs, loose, packCost, unitCost,
      batchQty, totalCost,
      expiryDate:   lineExpiry || null,
    }]);
    resetLineForm();
  };

  const removeLine = (idx) => {
    setPurchaseItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const invoiceTotal = useMemo(
    () => purchaseItems.reduce((s, l) => s + l.totalCost, 0),
    [purchaseItems]
  );

  const handleSavePurchase = async () => {
    if (purchaseItems.length === 0) {
      Swal.fire({ icon: 'warning', title: 'No items', text: 'Add at least one line item to save.' });
      return;
    }
    if (!purchaseDate) {
      Swal.fire({ icon: 'warning', title: 'Missing date', text: 'Pick a purchase date.' });
      return;
    }
    setSavingPurchase(true);
    try {
      const { data } = await axios.post(processConsumablePurchaseUrl, {
        purchaseDate,
        supplierName:  supplierName || null,
        invoiceNumber: invoiceNumber || null,
        receivedBy:    currentUser || null,
        items: purchaseItems.map(l => ({
          consumableId: l.consumableId,
          packs:        l.packs,
          loose:        l.loose,
          packCost:     l.packCost,
          unitCost:     l.unitCost,
          expiryDate:   l.expiryDate,
        })),
      });
      if (data?.success) {
        Swal.fire({ icon: 'success', title: 'Saved', text: data.message, timer: 1800, showConfirmButton: false });
        setPurchaseItems([]);
        resetLineForm();
        setSupplierName('');
        setInvoiceNumber('');
        setOpenPurchaseDialog(false);
        loadBatches();
      } else {
        Swal.fire({ icon: 'error', title: 'Save failed', text: data?.message || 'Unknown error' });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: err?.response?.data?.message || err.message });
    } finally {
      setSavingPurchase(false);
    }
  };

  // ── Reverse batch (admin password prompt) ────────────────────────────────
  const handleReverse = async (row) => {
    const { value: formValues } = await Swal.fire({
      title: 'Reverse this batch?',
      html: `
        <p style="text-align:left;color:#475569;font-size:13px;">
          This will subtract <strong>${row.batch_qty} ${row.base_unit}${row.batch_qty === 1 ? '' : 's'}</strong>
          from <strong>${row.consumable_name}</strong> and mark the batch as deleted.
        </p>
        <input id="rev-user" class="swal2-input" placeholder="Your username">
        <input id="rev-pass" type="password" class="swal2-input" placeholder="Password">
      `,
      showCancelButton: true,
      confirmButtonText: 'Reverse',
      confirmButtonColor: '#b91c1c',
      preConfirm: () => ({
        username: document.getElementById('rev-user').value,
        password: document.getElementById('rev-pass').value,
      }),
    });
    if (!formValues) return;
    try {
      const { data } = await axios.delete(deleteConsumablePurchaseUrl(row.trans_code), {
        data: formValues,
      });
      if (data?.success) {
        Swal.fire({ icon: 'success', title: 'Reversed', text: data.message, timer: 1800, showConfirmButton: false });
        loadBatches();
      } else {
        Swal.fire({ icon: 'error', title: 'Failed', text: data?.message });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: err?.response?.data?.message || err.message });
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  const pagedRows = batches.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  return (
    <Box sx={{ p: 2, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
            <ReceiptLong sx={{ color: PRIMARY, fontSize: 18 }} />
            <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>
              Procurement · Consumables
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: STRONG, lineHeight: 1.15 }}>
            Purchase Orders — Consumables
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: MUTED, mt: 0.5 }}>
            Receive cotton, plaster, cannula and other supplies into stock. Each invoice line writes a traceable batch and bumps the master stock count.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Reload">
            <span>
              <IconButton size="small" onClick={loadBatches} disabled={loading}
                sx={{ border: `1px solid ${BORDER}`, bgcolor: '#fff', color: MUTED }}>
                {loading ? <CircularProgress size={14} sx={{ color: PRIMARY }} /> : <Refresh fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained" disableElevation
            startIcon={<Add sx={{ fontSize: 18 }} />}
            onClick={() => setOpenPurchaseDialog(true)}
            sx={{
              textTransform: 'none', fontWeight: 700,
              bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK },
            }}>
            New purchase
          </Button>
        </Stack>
      </Box>

      {/* Stat tiles */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          { label: 'Total Batches',   value: stats.records.toLocaleString(),  icon: <ReceiptLong />,   tint: '#f1f5f9',                color: STRONG    },
          { label: 'Units Received',  value: stats.totalQty.toLocaleString(), icon: <TrendingUp />,    tint: 'rgba(16,185,129,0.10)',  color: '#15803D' },
          { label: 'Total Value',     value: formatCurrency(stats.totalValue), icon: <AttachMoney />,  tint: 'rgba(217,119,6,0.10)',   color: '#A16207' },
          { label: 'Expiring ≤ 90d',  value: stats.expiring.toLocaleString(), icon: <WarningAmber />,  tint: 'rgba(220,38,38,0.10)',   color: '#991B1B' },
        ].map(t => (
          <Grid item xs={6} md={3} key={t.label}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff', display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: t.tint, color: t.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {t.icon}
              </Box>
              <Box>
                <Typography sx={LABEL_SX}>{t.label}</Typography>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: STRONG, mt: 0.25 }} noWrap>
                  {t.value}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.25} flexWrap="wrap" alignItems="center">
          <TextField
            size="small"
            placeholder="Search by item, supplier or invoice #"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 280, flex: 1 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: MUTED }} /></InputAdornment>,
              endAdornment: searchTerm ? (
                <IconButton size="small" onClick={() => setSearchTerm('')}><Clear fontSize="small" /></IconButton>
              ) : null,
            }}
          />
          <TextField
            size="small" type="date" label="From"
            value={startDate} onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }}
          />
          <TextField
            size="small" type="date" label="To"
            value={endDate} onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="deleted">Reversed</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Table */}
      <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Date</TableCell>
                <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Item</TableCell>
                <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Supplier · Invoice</TableCell>
                <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Received</TableCell>
                <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Unit Cost</TableCell>
                <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Total</TableCell>
                <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Expiry</TableCell>
                <TableCell align="right" sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 6, textAlign: 'center', color: MUTED }}>
                    <ShoppingCartOutlined sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: STRONG }}>
                      No purchase batches to show
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                      Click "New purchase" to receive the first invoice.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map((row) => (
                  <TableRow key={row.trans_code} hover sx={{ opacity: row.status === 'deleted' ? 0.55 : 1 }}>
                    <TableCell sx={{ py: 1.2, fontSize: '0.78rem', color: STRONG, fontVariantNumeric: 'tabular-nums' }}>
                      {row.purchase_date}
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG }}>
                        {row.consumable_name || `Consumable #${row.consumable_id}`}
                      </Typography>
                      {row.category && (
                        <Typography sx={{ fontSize: '0.7rem', color: MUTED, mt: 0.1 }}>
                          {row.category}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1.2, fontSize: '0.78rem', color: STRONG }}>
                      {row.supplier || <span style={{ color: '#cbd5e1' }}>—</span>}
                      {row.invoice_no && (
                        <Typography sx={{ fontSize: '0.7rem', color: MUTED }}>
                          # {row.invoice_no}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: STRONG }}>
                        {formatStockLine(row.pack_qty, row.loose_qty, row.pack_label, row.base_unit)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: MUTED }}>
                        {row.batch_qty} {row.base_unit}{row.batch_qty === 1 ? '' : 's'} total
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.2, fontSize: '0.8rem', color: STRONG, fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrency(row.unit_cost)}
                    </TableCell>
                    <TableCell sx={{ py: 1.2, fontSize: '0.84rem', fontWeight: 700, color: STRONG, fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrency(row.total_cost)}
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      {row.expiry_date ? (
                        <Chip size="small"
                          icon={<Event sx={{ fontSize: 12 }} />}
                          label={row.expiry_date}
                          sx={{
                            height: 22, fontSize: '0.7rem', fontWeight: 700,
                            bgcolor: isExpiringSoon(row.expiry_date) ? 'rgba(220,38,38,0.10)' : '#f1f5f9',
                            color:   isExpiringSoon(row.expiry_date) ? '#991B1B' : STRONG,
                            '& .MuiChip-icon': { color: 'inherit' },
                          }} />
                      ) : (
                        <Typography sx={{ fontSize: '0.78rem', color: '#cbd5e1' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.2 }}>
                      {row.status === 'active' ? (
                        <Tooltip title="Reverse this batch (admin)">
                          <IconButton size="small" onClick={() => handleReverse(row)}
                            sx={{ color: '#b91c1c' }}>
                            <DeleteOutline sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Chip size="small" label="Reversed"
                          sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: 'rgba(220,38,38,0.10)', color: '#991B1B' }} />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={batches.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* New-purchase dialog (full screen, 2-pane) */}
      <Dialog
        open={openPurchaseDialog}
        onClose={closePurchaseDialog}
        maxWidth={false}
        PaperProps={{ sx: { borderRadius: 2, width: '95vw', height: '95vh', m: 0 } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1.25,
          px: 2, py: 1.5,
          bgcolor: 'rgba(65,163,134,0.10)', color: PRIMARY,
          borderBottom: `1px solid rgba(65,163,134,0.30)`,
        }}>
          <ShoppingCartOutlined sx={{ fontSize: 22 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>New Invoice</Typography>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: STRONG, lineHeight: 1.2 }}>
              Receive consumables into stock
            </Typography>
          </Box>
          <IconButton size="small" onClick={closePurchaseDialog}><Close fontSize="small" /></IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, bgcolor: '#f8fafc', display: 'flex', overflow: 'hidden' }}>
          {/* LEFT: catalogue */}
          <Box sx={{
            width: 360, borderRight: `1px solid ${BORDER}`,
            bgcolor: '#fff', display: 'flex', flexDirection: 'column',
          }}>
            <Box sx={{ p: 1.25, borderBottom: `1px solid ${BORDER}` }}>
              <TextField
                fullWidth size="small" placeholder="Search consumables…"
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: MUTED }} /></InputAdornment>,
                }}
              />
              <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                <InputLabel>Category</InputLabel>
                <Select label="Category" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                  <MenuItem value="all">All categories</MenuItem>
                  {categoryOptions.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {loadingConsumables ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <CircularProgress size={20} sx={{ color: PRIMARY }} />
                </Box>
              ) : filteredCatalogue.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center', color: MUTED }}>
                  <Inventory2 sx={{ fontSize: 32, color: '#cbd5e1', mb: 1 }} />
                  <Typography sx={{ fontSize: '0.82rem' }}>No consumables match.</Typography>
                </Box>
              ) : (
                filteredCatalogue.map((c) => {
                  const active = selectedItem?.id === c.id;
                  return (
                    <Box key={c.id}
                      onClick={() => { setSelectedItem(c); resetLineForm(); setSelectedItem(c); }}
                      sx={{
                        px: 1.5, py: 1, cursor: 'pointer',
                        borderBottom: `1px solid #f1f5f9`,
                        bgcolor: active ? 'rgba(65,163,134,0.10)' : 'transparent',
                        borderLeft: active ? `3px solid ${PRIMARY}` : '3px solid transparent',
                        '&:hover': { bgcolor: active ? 'rgba(65,163,134,0.14)' : '#f8fafc' },
                      }}>
                      <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: STRONG }}>
                        {c.name}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 0.25, flexWrap: 'wrap' }}>
                        {c.category && (
                          <Typography sx={{ fontSize: '0.7rem', color: MUTED }}>{c.category}</Typography>
                        )}
                        <Typography sx={{ fontSize: '0.7rem', color: MUTED }}>
                          {c.pack_size > 1 ? `${c.pack_label || 'Pack'}: ${c.pack_size} ${c.base_unit}s` : `Single ${c.base_unit}`}
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: '#15803D', fontWeight: 700 }}>
                          On hand: {c.stock_qty}
                        </Typography>
                      </Stack>
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>

          {/* RIGHT: invoice metadata + line form + line table */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Invoice metadata */}
            <Box sx={{ p: 1.5, bgcolor: '#fff', borderBottom: `1px solid ${BORDER}` }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" label="Purchase date" type="date"
                    value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" label="Supplier"
                    placeholder="e.g. Sino Medical Supplies"
                    value={supplierName} onChange={(e) => setSupplierName(e.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" label="Invoice #"
                    placeholder="e.g. INV-4521"
                    value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
                </Grid>
              </Grid>
            </Box>

            {/* Active line form */}
            {selectedItem ? (
              <Box sx={{ p: 1.5, bgcolor: '#fff', borderBottom: `1px solid ${BORDER}` }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Inventory2 sx={{ fontSize: 16, color: PRIMARY }} />
                  <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: STRONG }}>
                    {selectedItem.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: MUTED }}>
                    {selectedItem.pack_size > 1
                      ? `${selectedItem.pack_size} ${selectedItem.base_unit}s per ${selectedItem.pack_label || 'pack'}`
                      : `Single ${selectedItem.base_unit}`}
                  </Typography>
                </Stack>
                <Grid container spacing={1.5}>
                  {selectedItem.pack_size > 1 && (
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth size="small" type="number"
                        label={`${selectedItem.pack_label || 'Pack'}s`}
                        value={linePacks} onChange={(e) => setLinePacks(e.target.value)}
                        inputProps={{ min: 0 }} />
                    </Grid>
                  )}
                  <Grid item xs={12} md={selectedItem.pack_size > 1 ? 3 : 4}>
                    <TextField fullWidth size="small" type="number"
                      label={`Loose ${selectedItem.base_unit}s`}
                      value={lineLoose} onChange={(e) => setLineLoose(e.target.value)}
                      inputProps={{ min: 0 }} />
                  </Grid>
                  {selectedItem.pack_size > 1 && (
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth size="small" type="number"
                        label="Cost per pack"
                        value={linePackCost} onChange={(e) => setLinePackCost(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start">₵</InputAdornment> }} />
                    </Grid>
                  )}
                  <Grid item xs={12} md={selectedItem.pack_size > 1 ? 3 : 4}>
                    <TextField fullWidth size="small" type="number"
                      label="Cost per unit"
                      value={lineUnitCost} onChange={(e) => setLineUnitCost(e.target.value)}
                      helperText={selectedItem.pack_size > 1 ? 'Optional — derived from pack cost' : ''}
                      InputProps={{ startAdornment: <InputAdornment position="start">₵</InputAdornment> }} />
                  </Grid>
                  <Grid item xs={12} md={selectedItem.pack_size > 1 ? 12 : 4}>
                    <TextField fullWidth size="small" type="date" label="Expiry date (optional)"
                      value={lineExpiry} onChange={(e) => setLineExpiry(e.target.value)}
                      InputLabelProps={{ shrink: true }} />
                  </Grid>
                </Grid>
                <Stack direction="row" spacing={1} sx={{ mt: 1.25, justifyContent: 'flex-end' }}>
                  <Button size="small" onClick={resetLineForm}
                    sx={{ textTransform: 'none', color: MUTED }}>
                    Clear
                  </Button>
                  <Button size="small" variant="contained" disableElevation
                    startIcon={<Add sx={{ fontSize: 16 }} />}
                    onClick={handleAddLine}
                    sx={{ textTransform: 'none', fontWeight: 700, bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK } }}>
                    Add to invoice
                  </Button>
                </Stack>
              </Box>
            ) : (
              <Box sx={{ p: 3, bgcolor: '#fff', borderBottom: `1px solid ${BORDER}`, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.84rem', color: MUTED }}>
                  Pick a consumable on the left to add a line.
                </Typography>
              </Box>
            )}

            {/* Line items table */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, bgcolor: '#f8fafc' }}>
              <Paper variant="outlined" sx={{ borderColor: BORDER }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, borderBottom: `1px solid ${BORDER}` }}>
                  <Typography sx={{ ...LABEL_SX, color: STRONG }}>
                    Invoice lines · {purchaseItems.length}
                  </Typography>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: PRIMARY }}>
                    {formatCurrency(invoiceTotal)}
                  </Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...LABEL_SX, bgcolor: '#fff' }}>Item</TableCell>
                      <TableCell sx={{ ...LABEL_SX, bgcolor: '#fff' }}>Qty</TableCell>
                      <TableCell sx={{ ...LABEL_SX, bgcolor: '#fff' }}>Unit cost</TableCell>
                      <TableCell sx={{ ...LABEL_SX, bgcolor: '#fff' }}>Total</TableCell>
                      <TableCell sx={{ ...LABEL_SX, bgcolor: '#fff' }}>Expiry</TableCell>
                      <TableCell sx={{ ...LABEL_SX, bgcolor: '#fff' }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {purchaseItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 4, textAlign: 'center', color: MUTED }}>
                          <Typography sx={{ fontSize: '0.82rem' }}>
                            No lines yet — pick an item and click "Add to invoice".
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : purchaseItems.map((l, i) => (
                      <TableRow key={i} hover>
                        <TableCell sx={{ fontSize: '0.82rem', fontWeight: 700, color: STRONG }}>
                          {l.name}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', color: STRONG, fontVariantNumeric: 'tabular-nums' }}>
                          {formatStockLine(l.packs, l.loose, l.pack_label, l.base_unit)}
                          <Typography sx={{ fontSize: '0.68rem', color: MUTED }}>
                            = {l.batchQty} {l.base_unit}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', color: STRONG, fontVariantNumeric: 'tabular-nums' }}>
                          {formatCurrency(l.unitCost)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.82rem', fontWeight: 700, color: STRONG, fontVariantNumeric: 'tabular-nums' }}>
                          {formatCurrency(l.totalCost)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.74rem', color: l.expiryDate ? STRONG : '#cbd5e1' }}>
                          {l.expiryDate || '—'}
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => removeLine(i)} sx={{ color: '#b91c1c' }}>
                            <Close sx={{ fontSize: 16 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.25, bgcolor: '#fff', borderTop: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: '0.78rem', color: MUTED, mr: 'auto' }}>
            {purchaseItems.length} line{purchaseItems.length === 1 ? '' : 's'} · <strong style={{ color: STRONG }}>{formatCurrency(invoiceTotal)}</strong>
          </Typography>
          <Button onClick={closePurchaseDialog} disabled={savingPurchase} sx={{ textTransform: 'none', color: MUTED }}>
            Cancel
          </Button>
          <Button
            onClick={handleSavePurchase}
            disabled={savingPurchase || purchaseItems.length === 0}
            variant="contained" disableElevation
            startIcon={savingPurchase ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <CheckCircle sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK } }}>
            {savingPurchase ? 'Saving…' : 'Save invoice'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsumablePurchaseOrders;
